import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronRight, Trash2, FileText, Pencil, Copy } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/hooks/use-toast";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

interface Props {
  canManage: boolean;
  onGenerateInvoices: (categoryId: string) => void;
  onEdit?: (categoryId: string) => void;
  onDuplicate?: (categoryId: string) => void;
}

export function FeeStructureList({ canManage, onGenerateInvoices, onEdit, onDuplicate }: Props) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data: categories, isLoading } = useQuery({
    queryKey: ["fee_categories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("fee_categories")
        .select("*, fee_structure_items(*, fee_cost_types(name)), fee_installments(*)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const delMut = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("fee_categories").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["fee_categories"] });
      toast({ title: "Fee category deleted" });
    },
    onError: (e: any) => toast({ title: "Delete failed", description: e.message, variant: "destructive" }),
  });

  if (isLoading) return <div className="text-muted-foreground p-4">Loading…</div>;
  if (!categories?.length) return <Card><CardContent className="py-8 text-center text-muted-foreground">No fee structures yet. Click "Manage Structure" to create one.</CardContent></Card>;

  return (
    <div className="space-y-3">
      {categories.map((cat: any) => {
        const isOpen = expanded === cat.id;
        const sem1 = cat.fee_structure_items.filter((i: any) => i.semester === "I").reduce((s: number, i: any) => s + Number(i.amount), 0);
        const sem2 = cat.fee_structure_items.filter((i: any) => i.semester === "II").reduce((s: number, i: any) => s + Number(i.amount), 0);
        return (
          <Card key={cat.id}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-2">
                <button onClick={() => setExpanded(isOpen ? null : cat.id)} className="flex items-start gap-2 text-left flex-1">
                  {isOpen ? <ChevronDown className="h-4 w-4 mt-1" /> : <ChevronRight className="h-4 w-4 mt-1" />}
                  <div>
                    <CardTitle className="text-base">{cat.name}</CardTitle>
                    <CardDescription className="flex flex-wrap gap-1 mt-1">
                      <Badge variant="outline">{cat.student_type.replace("_", " ")}</Badge>
                      <Badge variant="outline">{cat.academic_year}</Badge>
                      {cat.classes.map((c: string) => <Badge key={c} variant="secondary">{c}</Badge>)}
                    </CardDescription>
                  </div>
                </button>
                <div className="text-right text-sm">
                  <div>Sem I: <strong>TZS {sem1.toLocaleString()}</strong></div>
                  <div>Sem II: <strong>TZS {sem2.toLocaleString()}</strong></div>
                  <div className="text-xs text-muted-foreground">Annual: TZS {(sem1 + sem2).toLocaleString()}</div>
                </div>
              </div>
            </CardHeader>
            {isOpen && (
              <CardContent className="space-y-3">
                <div>
                  <h5 className="text-sm font-semibold mb-1">Components</h5>
                  <Table>
                    <TableHeader><TableRow><TableHead>Component</TableHead><TableHead>Semester</TableHead><TableHead>Amount</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {cat.fee_structure_items.map((it: any) => (
                        <TableRow key={it.id}><TableCell>{it.fee_cost_types?.name}</TableCell><TableCell>{it.semester}</TableCell><TableCell>TZS {Number(it.amount).toLocaleString()}</TableCell></TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <div>
                  <h5 className="text-sm font-semibold mb-1">Installments</h5>
                  {(["I", "II"] as const).map(sem => {
                    const rows = cat.fee_installments
                      .filter((i: any) => i.semester === sem)
                      .sort((a: any, b: any) => a.installment_no - b.installment_no);
                    if (!rows.length) return null;
                    const semTotal = rows.reduce((s: number, i: any) => s + Number(i.amount), 0);
                    return (
                      <div key={sem} className="mb-2">
                        <div className="text-xs text-muted-foreground mb-0.5">Semester {sem}</div>
                        <Table>
                          <TableHeader><TableRow><TableHead>#</TableHead><TableHead>Label</TableHead><TableHead>Date Range</TableHead><TableHead>Amount</TableHead></TableRow></TableHeader>
                          <TableBody>
                            {rows.map((i: any) => (
                              <TableRow key={i.id}>
                                <TableCell>{i.installment_no}</TableCell>
                                <TableCell>{i.label}</TableCell>
                                <TableCell>{i.start_date && i.end_date ? `${i.start_date} → ${i.end_date}` : i.due_date}</TableCell>
                                <TableCell>TZS {Number(i.amount).toLocaleString()}</TableCell>
                              </TableRow>
                            ))}
                            <TableRow className="font-semibold">
                              <TableCell colSpan={3}>Sem {sem} total</TableCell>
                              <TableCell>TZS {semTotal.toLocaleString()}</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </div>
                    );
                  })}
                </div>
                {canManage && (
                  <div className="flex gap-2 justify-end">
                    <Button size="sm" variant="outline" onClick={() => onGenerateInvoices(cat.id)}><FileText className="mr-1 h-3 w-3" />Generate Invoices</Button>
                    {onEdit && <Button size="sm" variant="outline" onClick={() => onEdit(cat.id)}><Pencil className="mr-1 h-3 w-3" />Edit</Button>}
                    {onDuplicate && <Button size="sm" variant="outline" onClick={() => onDuplicate(cat.id)}><Copy className="mr-1 h-3 w-3" />Duplicate</Button>}
                    <Button size="sm" variant="ghost" onClick={() => setDeleteId(cat.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        );
      })}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete fee structure?</AlertDialogTitle><AlertDialogDescription>This removes the category and all its components and installments. This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (deleteId) { delMut.mutate(deleteId); setDeleteId(null); } }}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
